/**
 * Cloudflare Turnstile — the anti-spam check on the contact form.
 *
 * It is deliberately optional: without VITE_TURNSTILE_SITE_KEY nothing is
 * rendered and no script is loaded, which is how the site runs in development.
 * The API mirrors that: with no secret configured it skips verification.
 *
 * Most visitors never interact with it — the widget solves itself and hands
 * back a token. The token is single-use, so it is reset after every attempt.
 */
import { useEffect, useRef } from 'react'

const SITE_KEY = (import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined) ?? ''
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'

export const turnstileEnabled = SITE_KEY !== ''

interface TurnstileApi {
  render: (element: HTMLElement, options: Record<string, unknown>) => string
  reset: (widgetId?: string) => void
  remove: (widgetId?: string) => void
}

declare global {
  interface Window {
    turnstile?: TurnstileApi
  }
}

let loading: Promise<TurnstileApi | null> | null = null

/**
 * Loads the widget script once per page, no matter how many times the
 * component mounts, and waits for the API object to actually appear — the
 * script sets it slightly after "load" fires.
 */
function loadTurnstile(): Promise<TurnstileApi | null> {
  if (typeof document === 'undefined') return Promise.resolve(null)
  if (window.turnstile) return Promise.resolve(window.turnstile)

  loading ??= new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${SCRIPT_SRC}"]`)
    if (existing) {
      existing.addEventListener('load', () => resolve())
      existing.addEventListener('error', () => reject(new Error('turnstile failed to load')))
      return
    }
    const script = document.createElement('script')
    script.src = SCRIPT_SRC
    script.async = true
    script.defer = true
    script.addEventListener('load', () => resolve())
    script.addEventListener('error', () => reject(new Error('turnstile failed to load')))
    document.head.appendChild(script)
  })
    .then(() => waitForApi())
    .catch(() => null)

  return loading
}

function waitForApi(timeoutMs = 10_000): Promise<TurnstileApi | null> {
  return new Promise((resolve) => {
    const started = Date.now()
    const tick = () => {
      if (window.turnstile) return resolve(window.turnstile)
      if (Date.now() - started > timeoutMs) return resolve(null)
      window.setTimeout(tick, 50)
    }
    tick()
  })
}

export function Turnstile({
  locale,
  resetKey = 0,
  onToken,
}: {
  locale: string
  /** Change this to clear a spent token and ask for a fresh one. */
  resetKey?: number
  onToken: (token: string) => void
}) {
  const container = useRef<HTMLDivElement>(null)
  const widgetId = useRef<string | null>(null)

  // Kept in a ref so a new inline callback on every render does not tear the
  // widget down and build it again.
  const emit = useRef(onToken)
  useEffect(() => {
    emit.current = onToken
  }, [onToken])

  useEffect(() => {
    if (!turnstileEnabled) return
    let cancelled = false

    void loadTurnstile().then((api) => {
      if (cancelled || !api || !container.current || widgetId.current !== null) return
      widgetId.current = api.render(container.current, {
        sitekey: SITE_KEY,
        language: locale,
        callback: (token: string) => emit.current(token),
        // A token expires after a few minutes; an empty string tells the form
        // it no longer has one.
        'expired-callback': () => emit.current(''),
        'error-callback': () => emit.current(''),
      })
    })

    return () => {
      cancelled = true
      if (widgetId.current !== null && window.turnstile) {
        window.turnstile.remove(widgetId.current)
        widgetId.current = null
      }
    }
  }, [locale])

  useEffect(() => {
    if (resetKey === 0 || widgetId.current === null || !window.turnstile) return
    window.turnstile.reset(widgetId.current)
    emit.current('')
  }, [resetKey])

  if (!turnstileEnabled) return null
  // The height is reserved up front so the button below does not jump when the
  // widget finishes loading.
  return <div ref={container} className="min-h-[65px]" />
}
